import { FC, KeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import { Form } from 'react-bootstrap';

import ELEMENT, { createTestId } from 'common/element';

import './Input.scss';

type InputProps = {
  label?:              string | ReactNode;
  value?:              string;
  onChange?:           (event: SyntheticEvent) => void;
  onFocus?:            JSX.IntrinsicElements['input']['onFocus'];
  onBlur?:             JSX.IntrinsicElements['input']['onBlur'];
  id?:                 string;
  groupId?:            string;
  inputClassName?:     string;
  labelClassName?:     string;
  labelTextClassName?: string;
  errorTextClassName?: string;
  validTextClassName?: string;
  disabled?:           boolean;
  placeholder?:        string;
  isInvalid?:          boolean;
  isValid?:            boolean;
  name?:               string;
  type?:               string;
  size?:               'lg' | 'sm';
  errorMessage?:       string;
  validMessage?:       string;
  onPressEnter?:       () => void;
  min?:                string;
  testId?:             ELEMENT;
};

/**
 * This component is a simple text INPUT possibly preceded by a label. It can be
 * used to get a single string value from the user.
 *
 * @prop label                     label text for the input
 * @prop value                     starting value for the input field
 * @prop placeholder               placeholder for the input field
 * @prop onChange                  function to call when value changes
 * @prop id                        DOM ID for the input
 * @prop isInvalid                 Change styles if input not validated
 * @prop name                      DOM name for the input
 * @prop type                      DOM type for the input
 * @prop errorMessage              error message for the validation input
 * @prop labelClassName            classname to style label HTML element
 * @prop inputClassName            classname to style input HTML element
 * @prop labelTextClassName        classname to style span with label text
 * @prop errorTextClassName        classname to style error message
 * @prop style                     attribute accepts a JavaScript object with camelCased properties rather than a CSS string.
 * @prop min                       The min attribute specifies the minimum value for an <input> element.
 */
const Input: FC<InputProps> = ({
  label,
  value = '',
  onChange,
  onFocus,
  onBlur,
  id,
  groupId,
  inputClassName = '',
  labelClassName = '',
  labelTextClassName = '',
  errorTextClassName = '',
  validTextClassName = '',
  disabled = false,
  placeholder = '',
  isInvalid,
  isValid,
  min,
  name = '',
  type = 'text',
  size,
  errorMessage,
  validMessage,
  onPressEnter,
  testId,
}: InputProps): JSX.Element => {
  const labelEl = label ? <Form.Label className={labelTextClassName}>{label}</Form.Label> : null;

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && onPressEnter) {
      e.preventDefault();
      onPressEnter();
    }
  };

  const invalidInputTestId = createTestId(testId, 'invalid');

  return (
    <Form.Group className={`Form__Group ${labelClassName}`} id={groupId}>
      {labelEl}
      <Form.Control
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        type={type}
        id={id}
        min={min}
        name={name}
        isInvalid={isInvalid}
        isValid={isValid}
        size={size}
        className={inputClassName}
        onKeyPress={onKeyPress}
        data-test-id={testId}
      />
      <Form.Control.Feedback type="invalid" className={errorTextClassName} data-test-id={invalidInputTestId}>{errorMessage}</Form.Control.Feedback>
      <Form.Control.Feedback type="valid" className={validTextClassName}>{validMessage}</Form.Control.Feedback>
    </Form.Group>
  );
};

export default Input;
